export type ScannedItemMatch<T> =
	| { item: T; kind: "matched" }
	| { count: number; kind: "ambiguous" }
	| { kind: "none" };

type ScannedCodes = readonly string[] | string | null | undefined;

export interface ScannedItemFields<T> {
	codes: ((item: T) => ScannedCodes)[];
	label: (item: T) => string;
}

const normalize = (value: string | null | undefined): string => value?.trim().toLowerCase() ?? "";

const matchScannedItem = <T>(items: T[], code: string, fields: ScannedItemFields<T>): ScannedItemMatch<T> => {
	const normalizedCode = normalize(code);
	if (!normalizedCode) return { kind: "none" };

	const tiers: ((item: T) => boolean)[] = [
		...fields.codes.map(
			(getCodes) => (item: T) => [getCodes(item)].flat().some((code) => normalize(code) === normalizedCode)
		),
		(item: T) => normalize(fields.label(item)) === normalizedCode,
		(item: T) => normalize(fields.label(item)).includes(normalizedCode)
	];

	for (const tier of tiers) {
		const candidates = items.filter(tier);
		if (candidates.length === 1) return { item: candidates[0], kind: "matched" };
		if (candidates.length > 1) return { count: candidates.length, kind: "ambiguous" };
	}

	return { kind: "none" };
};

interface ScannableItem {
	absorbedSkus?: readonly string[];
	absorbedUpcs?: readonly string[];
	sku: string;
	upc: string;
}

interface ScannedCodeMatchers {
	isScannedSku: (sku: string) => boolean;
	isScannedUpc: (upc: string) => boolean;
}

const ownCodeFirstTiers = <T extends ScannableItem>({
	isScannedSku,
	isScannedUpc
}: ScannedCodeMatchers): ((item: T) => boolean)[] => [
	(item) => isScannedUpc(item.upc) || isScannedSku(item.sku),
	(item) => (item.absorbedUpcs ?? []).some(isScannedUpc) || (item.absorbedSkus ?? []).some(isScannedSku)
];

export const findItemByOwnCodeFirst = <T extends ScannableItem>(
	items: readonly T[],
	matchers: ScannedCodeMatchers
): T | undefined => {
	for (const tier of ownCodeFirstTiers<T>(matchers)) {
		const item = items.find(tier);
		if (item) return item;
	}

	return undefined;
};

export type OwnCodeMatch<T> = { candidates: T[]; kind: "ambiguous" } | { item: T; kind: "matched" } | { kind: "none" };

export const matchItemByOwnCodeFirst = <T extends ScannableItem>(
	items: readonly T[],
	matchers: ScannedCodeMatchers
): OwnCodeMatch<T> => {
	for (const tier of ownCodeFirstTiers<T>(matchers)) {
		const candidates = items.filter(tier);
		if (candidates.length === 1) return { item: candidates[0], kind: "matched" };
		if (candidates.length > 1) return { candidates, kind: "ambiguous" };
	}

	return { kind: "none" };
};

export default matchScannedItem;
